<script setup>
import { computed } from 'vue'
import { router } from '@inertiajs/vue3'
import AppLayout from '@/layouts/AppLayout.vue'

const props = defineProps({
  equipement:           { type: Object,  default: () => ({}) },
  can_update:           { type: Boolean, default: false },
  can_see_costs:        { type: Boolean, default: false },
  show_type_equipement: { type: Boolean, default: false },
  show_description:     { type: Boolean, default: false },
  dependant:            { type: Boolean, default: false },
})

const destroyLabel = computed(() => {
  if (!props.equipement.actif) return 'Activer'
  return props.dependant ? 'Désactiver' : 'Effacer'
})

const destroyIcon = computed(() => {
  if (!props.equipement.actif) return 'toggle_on'
  return props.dependant ? 'toggle_off' : 'delete'
})

const destroyVariant = computed(() => {
  if (!props.equipement.actif) return 'text-tertiary border-tertiary/30 hover:bg-tertiary/10'
  return props.dependant
    ? 'text-yellow-500 border-yellow-500/30 hover:bg-yellow-500/10'
    : 'text-error border-error/30 hover:bg-error/10'
})

function confirmDestroy() {
  const msg = props.equipement.actif
    ? (props.dependant ? `Désactiver l'équipement ${props.equipement.code} ?` : `Effacer l'équipement ${props.equipement.code} ?`)
    : `Réactiver l'équipement ${props.equipement.code} ?`
  if (!confirm(msg)) return
  router.delete(`/equipements/${props.equipement.id}`)
}
</script>

<template>
  <AppLayout>
    <template #topbar-left>
      <div class="text-xs font-black uppercase tracking-widest text-outline">
        Équipements &rsaquo; {{ equipement.code }}
      </div>
    </template>

    <div class="p-8">
      <div class="max-w-4xl mx-auto">

        <!-- Page Header -->
        <div class="mb-8 flex justify-between items-end">
          <div>
            <h1 class="text-3xl font-black text-on-surface tracking-tight uppercase">
              {{ equipement.nom }}
            </h1>
            <p class="text-outline text-sm font-black uppercase tracking-widest mt-1">
              {{ equipement.code }}
            </p>
          </div>
          <div class="flex items-center gap-3">
            <!-- Bouton Désactiver/Effacer/Activer — commenté temporairement
            <button
              v-if="can_update"
              @click="confirmDestroy"
              :class="['flex items-center gap-2 px-4 py-2 text-xs font-black uppercase tracking-widest rounded-md border bg-transparent transition-all', destroyVariant]"
            >
              <span class="material-symbols-outlined text-base">{{ destroyIcon }}</span>
              {{ destroyLabel }}
            </button>
            -->
            <a
              v-if="can_update"
              :href="`/equipements/${equipement.id}/edit`"
              class="flex items-center gap-2 px-4 py-2 text-xs font-black uppercase tracking-widest text-outline hover:text-on-surface bg-surface-container-low border border-outline-variant/10 rounded-md transition-all"
            >
              <span class="material-symbols-outlined text-base">edit</span>
              Modifier
            </a>
            <a
              href="/equipements"
              class="flex items-center gap-2 px-4 py-2 text-xs font-black uppercase tracking-widest text-outline hover:text-on-surface bg-surface-container-low border border-outline-variant/10 rounded-md transition-all"
            >
              <span class="material-symbols-outlined text-base">arrow_back</span>
              Retour
            </a>
          </div>
        </div>

        <!-- Detail Card -->
        <div class="bg-surface-container rounded-xl border-l-4 border-primary shadow-2xl shadow-black/40 overflow-hidden">
          <div class="divide-y divide-outline-variant/10">

            <div class="flex items-center px-8 py-4 gap-6">
              <span class="text-[10px] font-black uppercase tracking-widest text-outline w-40 shrink-0">Code</span>
              <span class="text-sm text-on-surface font-mono font-bold text-primary">{{ equipement.code }}</span>
            </div>

            <div class="flex items-center px-8 py-4 gap-6">
              <span class="text-[10px] font-black uppercase tracking-widest text-outline w-40 shrink-0">Nom</span>
              <span class="text-sm text-on-surface">{{ equipement.nom || '—' }}</span>
            </div>

            <div v-if="show_type_equipement" class="flex items-center px-8 py-4 gap-6">
              <span class="text-[10px] font-black uppercase tracking-widest text-outline w-40 shrink-0">Type</span>
              <span class="text-sm text-on-surface font-bold">{{ equipement.type_equipement || '—' }}</span>
            </div>

            <div v-if="show_description" class="flex items-start px-8 py-4 gap-6">
              <span class="text-[10px] font-black uppercase tracking-widest text-outline w-40 shrink-0 pt-0.5">Description</span>
              <span class="text-sm text-on-surface/80 whitespace-pre-line">{{ equipement.description || '—' }}</span>
            </div>

            <div v-if="can_see_costs" class="flex items-center px-8 py-4 gap-6">
              <span class="text-[10px] font-black uppercase tracking-widest text-outline w-40 shrink-0">Taux horaire</span>
              <span class="text-sm text-on-surface font-bold">
                {{ equipement.taux_horaire ? `${equipement.taux_horaire} $/h` : '—' }}
              </span>
            </div>

            <div class="flex items-center px-8 py-4 gap-6">
              <span class="text-[10px] font-black uppercase tracking-widest text-outline w-40 shrink-0">Statut</span>
              <span
                :class="equipement.actif
                  ? 'bg-tertiary/15 text-tertiary border border-tertiary/30'
                  : 'bg-outline/10 text-outline border border-outline/20'"
                class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest"
              >
                <span class="w-1.5 h-1.5 rounded-full" :class="equipement.actif ? 'bg-tertiary' : 'bg-outline/50'"></span>
                {{ equipement.actif ? 'Actif' : 'Inactif' }}
              </span>
            </div>

          </div>
        </div>

        <!-- Footer -->
        <footer class="mt-12 pb-6 text-center text-outline text-[10px] uppercase tracking-widest font-black">
          Copyright &copy; 2016-{{ new Date().getFullYear() }} aurapport.com inc.
        </footer>

      </div>
    </div>
  </AppLayout>
</template>
